/** GL-TOOL-003 — Upsell Recommendation Cards (spec-faithful to GL-TOOL-003-MAP).
 *
 * Renders up to 3 service cards on the Report tab. Primary cards (Doc Dev, Compliance
 * Readiness Visit, Control System) render larger. Incident Review is isolated by the
 * backend — when it fires, no other cards are returned.
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, formatApiError } from '@/lib/api';
import { toast } from 'sonner';
import {
  HandshakeIcon,
  XIcon,
  ArrowSquareOutIcon,
  EnvelopeSimpleIcon,
  FirstAidIcon,
  CaretRightIcon,
} from '@phosphor-icons/react';

const SERVICE_BLURB = {
  doc_development: 'GigLine writes the programs you are missing.',
  compliance_readiness_visit: 'Floor and files reviewed in a single visit.',
  control_system: 'Complete safety infrastructure — built once, maintained forever.',
  annual_partner: 'Year-round consultant, quarterly visits, single point of contact.',
  quarterly_maintenance: 'On-site before every deadline. Inspection-ready year-round.',
  incident_review: 'Post-incident root cause + OSHA recordability determination.',
};

export default function UpsellCards({ eng, compact = false }) {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);

  async function load() {
    try {
      const { data } = await api.get(`/engagements/${eng.id}/upsell_recommendations`);
      // Backend already returns priority-ranked + capped, but filter active just in case.
      setItems(data.filter((r) => r.status === 'active'));
    } catch (e) {
      setItems([]);
    }
  }
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/engagements/${eng.id}/upsell_recommendations`);
        if (!cancelled) setItems(data.filter((r) => r.status === 'active'));
      } catch (e) {
        if (!cancelled) setItems([]);
      }
    })();
    return () => { cancelled = true; };
  }, [eng.id]);
  /* eslint-enable react-hooks/exhaustive-deps */

  async function dismiss(rec) {
    try {
      await api.patch(`/upsell_recommendations/${rec.id}/dismiss`);
      setItems((it) => it.filter((x) => x.id !== rec.id));
      toast.success('Card dismissed');
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function createIncident() {
    if (!window.confirm(`Flag an incident at ${eng.business_name}?\n\nThis creates a linked Incident Investigation engagement and surfaces the Incident Review upsell card here.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/engagements/${eng.id}/incident`);
      toast.success('Incident logged');
      await load();
      if (window.confirm('Open the new Incident Investigation engagement now?')) {
        nav(`/engagements/${data.id}`);
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  if (items === null) return null;

  // Per spec, Incident button is available on the Report tab whenever an engagement exists.
  const incidentBtnVisible = !!eng.id;

  if (items.length === 0 && !incidentBtnVisible) return null;

  return (
    <div className={compact ? '' : 'mb-5'} data-testid="upsell-cards">
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        {!compact && <HandshakeIcon size={22} weight="fill" style={{ color: 'var(--gl-gold)' }} />}
        <div
          className={`font-display font-extrabold tracking-widest ${compact ? 'text-base' : 'uppercase text-lg'}`}
          style={{ color: 'var(--gl-dark)', letterSpacing: compact ? '-0.01em' : '0.08em' }}
        >
          {compact ? 'Next steps to offer' : `Next Steps for ${eng.business_name}`}
        </div>
        {!compact && (
          <span
            className="text-xs uppercase tracking-widest font-bold px-2 py-1"
            style={{ background: 'var(--gl-dark-navy)', color: '#fff', fontFamily: 'Inter', letterSpacing: '0.1em' }}
          >
            Internal Only — Not Shown to Client
          </span>
        )}
        {incidentBtnVisible && (
          <button
            type="button"
            className="ml-auto gl-btn gl-btn-secondary !h-10 !px-4 !text-xs"
            onClick={createIncident}
            disabled={busy}
            data-testid="client-incident-btn"
            title="Flag an incident — surfaces the isolated Incident Review card and creates a linked engagement."
            style={{ color: 'var(--gl-p1)', borderColor: 'var(--gl-p1)' }}
          >
            <FirstAidIcon size={16} weight="fill" /> Client Had an Incident
          </button>
        )}
      </div>

      {items.length === 0 ? (
        <div className="gl-card text-sm" style={{ color: 'var(--gl-muted)' }}>
          No upsell triggers fired for this engagement. Cards refresh automatically when you mark it Delivered.
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((rec) => (
            <UpsellCard
              key={rec.id}
              rec={rec}
              eng={eng}
              onDismiss={() => dismiss(rec)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function UpsellCard({ rec, eng, onDismiss }) {
  const isPrimary = rec.tier === 'primary';
  const isIncident = rec.service_key === 'incident_review';

  function sendQuote() {
    const subjectMap = {
      doc_development: `GigLine — Document Development Quote Request for ${eng.business_name}`,
      annual_partner: `GigLine — Annual Compliance Partnership — ${eng.business_name}`,
      incident_review: `GigLine — Incident Review Support — ${eng.business_name}`,
    };
    const subject = encodeURIComponent(subjectMap[rec.service_key] || `GigLine — Next Step for ${eng.business_name}`);
    const lines = [
      `Hey,`,
      ``,
      `Following the recent engagement, the next step I'd recommend is the ${rec.service_name} (${rec.service_price}).`,
      ``,
      `Why this came up:`,
      ...rec.reasons.map((r) => `• ${r}`),
    ];
    if (rec.triggering_findings && rec.triggering_findings.length > 0) {
      lines.push(``, `Triggering findings from your report:`);
      rec.triggering_findings.forEach((f) => {
        lines.push(`• Finding #${f.number} [${f.priority}] ${f.cfr_citation} — ${f.description}`);
      });
    }
    lines.push(
      ``,
      `Intake / more detail: ${rec.service_url}`,
      ``,
      `Happy to walk through scope before scheduling — call or text (336) 329-8899.`,
      ``,
      `— Vince Lawrence`,
      `GigLine Safety & Compliance`,
    );
    const body = encodeURIComponent(lines.join('\n'));
    const to = eng.contact_email || '';
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  }

  function bookService() {
    const url = new URL(rec.service_url);
    if (eng.business_name) url.searchParams.set('facility', eng.business_name);
    if (eng.contact_name) url.searchParams.set('contact_name', eng.contact_name);
    if (eng.contact_email) url.searchParams.set('contact_email', eng.contact_email);
    if (eng.contact_phone) url.searchParams.set('contact_phone', eng.contact_phone);
    window.open(url.toString(), '_blank', 'noopener,noreferrer');
  }

  // Card border: gold for primary/incident, muted navy for secondary.
  const borderColor = isIncident
    ? 'var(--gl-p1)'
    : isPrimary
      ? 'var(--gl-gold)'
      : 'var(--gl-navy)';

  return (
    <div
      className="gl-card"
      style={{
        borderLeft: `4px solid ${borderColor}`,
        background: isPrimary || isIncident ? '#fdf6e3' : '#fff',
      }}
      data-testid={`upsell-card-${rec.service_key}`}
    >
      <div className="flex items-baseline gap-2 flex-wrap mb-1">
        {isIncident && (
          <FirstAidIcon size={18} weight="fill" style={{ color: 'var(--gl-p1)' }} />
        )}
        <div
          className="font-display font-extrabold uppercase tracking-wide"
          style={{ color: 'var(--gl-dark)', letterSpacing: '0.05em', fontSize: isPrimary ? '1.25rem' : '1.05rem' }}
        >
          {rec.service_name}
        </div>
        <span
          className="text-xs uppercase font-bold ml-1"
          style={{
            color: isPrimary ? 'var(--gl-p3)' : 'var(--gl-muted)',
            fontFamily: 'Inter',
            letterSpacing: '0.08em',
          }}
        >
          {rec.service_price}
        </span>
        <span
          className="text-xs uppercase font-bold px-1.5 py-0.5 ml-auto"
          style={{
            background: isPrimary ? 'var(--gl-gold)' : '#e5e7eb',
            color: isPrimary ? 'var(--gl-dark)' : 'var(--gl-muted)',
            fontFamily: 'Inter',
            letterSpacing: '0.08em',
          }}
        >
          {isIncident ? 'Critical' : isPrimary ? 'Primary' : 'Secondary'}
        </span>
      </div>

      {SERVICE_BLURB[rec.service_key] && (
        <div className="text-xs uppercase tracking-wider font-bold mb-2"
             style={{ color: 'var(--gl-muted)', fontFamily: 'Inter', letterSpacing: '0.08em' }}>
          {SERVICE_BLURB[rec.service_key]}
        </div>
      )}

      <ul className="space-y-2 mb-3">
        {rec.reasons.map((r, idx) => (
          <li key={idx} className="text-sm flex gap-2" style={{ color: 'var(--gl-dark)' }}>
            <CaretRightIcon size={14} weight="bold" style={{ color: borderColor, flexShrink: 0, marginTop: 4 }} />
            <span>{r}</span>
          </li>
        ))}
      </ul>

      {rec.triggering_findings && rec.triggering_findings.length > 0 && (
        <div
          className="text-xs mb-3 p-2 border rounded-sm"
          style={{ borderColor: '#e5e7eb', background: '#fff', color: 'var(--gl-muted)' }}
        >
          <div
            className="font-bold uppercase tracking-wider mb-1"
            style={{ color: 'var(--gl-dark)', fontFamily: 'Inter', letterSpacing: '0.08em' }}
          >
            Triggering findings ({rec.triggering_findings.length})
          </div>
          {rec.triggering_findings.map((f, i) => (
            <div key={i}>
              #{f.number} [{f.priority}] {f.cfr_citation} — {f.description}
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {rec.button_type === 'send_quote' ? (
          <button
            type="button"
            className="gl-btn gl-btn-primary !h-10 !px-4 !text-xs"
            onClick={sendQuote}
            data-testid={`upsell-send-${rec.service_key}`}
          >
            <EnvelopeSimpleIcon size={14} weight="bold" /> Send Quote to Client
          </button>
        ) : (
          <button
            type="button"
            className="gl-btn gl-btn-primary !h-10 !px-4 !text-xs"
            onClick={bookService}
            data-testid={`upsell-book-${rec.service_key}`}
          >
            <ArrowSquareOutIcon size={14} weight="bold" /> Book This Service
          </button>
        )}
        <a
          href={rec.service_url}
          target="_blank"
          rel="noreferrer"
          className="gl-btn gl-btn-secondary !h-10 !px-3 !text-xs"
          data-testid={`upsell-intake-${rec.service_key}`}
          title="Open intake page"
        >
          Intake Page
        </a>
        <button
          type="button"
          className="ml-auto gl-btn gl-btn-secondary !h-10 !px-3 !text-xs"
          onClick={onDismiss}
          data-testid={`upsell-dismiss-${rec.service_key}`}
        >
          <XIcon size={14} weight="bold" /> Dismiss
        </button>
      </div>
    </div>
  );
}
